import React, { useEffect, useState } from 'react';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { TransferProps, GetProp, TableColumnsType, Space, Spin } from 'antd';
import { formatFullName, hasPermission } from 'utils/utils';
import { User } from 'types/userTypes';
import { profileIcon } from 'utils/visitorsHelper';
import { TableTransfer } from './UserTableTransfer';
import { useTranslation } from 'react-i18next';

type TransferItem = GetProp<TransferProps, 'dataSource'>[number];

interface UserFormProps {
    users: User[];
    usersInRole: User[];
    loading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ users, usersInRole, loading }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

    const hasRoleWrite = hasPermission(tokenScopeList, orgId, 'o', 'role:w');

    const [targetKeys, setTargetKeys] = useState<React.Key[]>(usersInRole.map((user: User) => user.id) || []);

    const { t } = useTranslation();

    // Sync targetKeys whenever usersInRole updates (fetched asynchronously by the
    // parent drawer). Without this, the transfer shows the previous role's users
    // because useState only reads its initial value on the first mount.
    useEffect(() => {
        setTargetKeys(usersInRole.map((user: User) => user.id));
    }, [usersInRole]);

    const columns: TableColumnsType<TransferItem> = [
        {
            dataIndex: 'fullName',
            title: t('role.form.name'),
            render: (text, record) => (
                <Space>
                    {profileIcon({
                        fullName: record.fullName,
                        size: 24,
                        color: isDarkMode ? '#D3D3D3' : '#1f93ff',
                    })}
                    {text}
                </Space>
            ),
        },
        {
            dataIndex: 'email',
            title: t('role.form.email'),
        },
    ];

    // While the parent is still fetching usersInRole, show a spinner instead of
    // the transfer. This prevents the visible "jump" where rows animate from the
    // available column into the in-role column once targetKeys populates.
    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <Spin />
            </div>
        );
    }

    return (
        <OpForm.Item name="users">
            <TableTransfer
                dataSource={users.map((user) => ({
                    key: user.id,
                    fullName: formatFullName(user.identity.firstName, user.identity.middleName, user.identity.lastName),
                    email: user.identity.email || '',
                }))}
                titles={[t('role.transfer.availableUsers'), t('role.transfer.usersInRole')]}
                targetKeys={targetKeys}
                showSearch
                showSelectAll={false}
                onChange={(nextTargetKeys) => setTargetKeys(nextTargetKeys)}
                filterOption={(inputValue, item) =>
                    item.fullName.toLowerCase().includes(inputValue.toLowerCase()) ||
                    item.email.toLowerCase().includes(inputValue.toLowerCase())
                }
                leftColumns={columns}
                rightColumns={columns}
                disabled={!hasRoleWrite}
            />
        </OpForm.Item>
    );
};

export default UserForm;
